import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  Calendar,
} from "lucide-react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      localStorage.setItem("slotmate_token", response.data.access_token);

      toast.success("Signed in successfully!");

      const meResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${response.data.access_token}` },
      });

      // Check user role and redirect accordingly
      const userData = meResponse.data;
      if (userData.role === "staff") {
        navigate("/staff");
      } else if (userData.business) {
        navigate("/dashboard");
      } else {
        navigate("/setup");
      }

      window.location.reload();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          error.response.data.detail || "Your subscription has expired.",
        );
        localStorage.removeItem("slotmate_token");
      } else {
        toast.error(
          error.response?.data?.detail || "Invalid email or password",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* TOP HEADER */}
      <div className="h-16 flex items-center justify-between px-6 lg:px-12 border-b bg-white">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Calendar className="w-5 h-5 text-white" />
          </div>

          <span className="font-heading font-bold text-xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            SlotBook
          </span>
        </Link>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex flex-1">
        {/* LEFT PANEL */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary" />

          <img
            src="https://images.unsplash.com/photo-1637777277337-f114350fb088?crop=entropy&cs=srgb&fm=jpg"
            alt="Modern salon"
            className="w-full h-full object-cover mix-blend-overlay"
          />

          <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
            <h2 className="font-heading text-4xl font-extrabold mb-4">
              Welcome Back
            </h2>

            <p className="text-lg text-white/80 max-w-md mb-8">
              Manage your salon bookings, staff and clients effortlessly with
              SlotBook.
            </p>

            <div className="flex flex-wrap gap-3">
              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm">
                ✓ Manage appointments easily
              </div>

              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm">
                ✓ Track your bookings
              </div>

              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm">
                ✓ Grow your salon business
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-slate-100">
          <div className="w-full max-w-lg bg-white border rounded-2xl shadow-xl p-8 lg:p-10">
            {/* TITLE */}
            <div className="mb-8 text-center">
              <h1 className="font-heading text-3xl font-extrabold mb-2">
                Sign in to your account
              </h1>

              <p className="text-muted-foreground">
                Enter your email and password to continue
              </p>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* EMAIL */}
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>

                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 text-base"
                    required
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                </div>

                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 text-base"
                    required
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* SUBMIT */}
              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>

              {/* FORGOT PASSWORD (moved here) */}
              <div className="text-center">
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              {/* REGISTER */}
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{" "}
                <Link
                  to="/auth/register"
                  className="text-primary font-medium hover:underline"
                >
                  Register your business
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
